import { useCallback, useEffect } from 'react'
import { InterviewStatus } from '@bosmart/types'
import { handleError, SessionError } from '@/lib/errors'
import { logger } from '@/lib/logging'
import { createLiveService } from './createLiveService'
import { loadInterviewContext } from './loadInterviewContext'
import type { InterviewSessionOptions, SessionRefs, SessionSetters, SessionUiState } from './types'

interface UseLiveSessionArgs
  extends Pick<InterviewSessionOptions, 'interviewId' | 'token' | 'otpProofToken'> {
  refs: SessionRefs
  state: SessionUiState
  setters: SessionSetters
  apiBaseUrl: string
  showNotification: (message: string) => void
  startVideoStreaming: () => void
}

const MAX_RECONNECT_ATTEMPTS = 5

export function useLiveSession({
  refs,
  state,
  setters,
  interviewId,
  token,
  otpProofToken,
  apiBaseUrl,
  showNotification,
  startVideoStreaming,
}: UseLiveSessionArgs) {
  const scheduleReconnect = useCallback(
    (reason: string) => {
      if (
        !refs.shouldReconnectRef.current ||
        state.showThankYou ||
        state.status === InterviewStatus.ENDED
      )
        return
      if (refs.isIntentionallyDisconnectingRef.current || refs.isConnectingRef.current) return

      if (refs.reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        logger.error('Max reconnect attempts reached, stopping reconnection', {
          attempts: refs.reconnectAttemptsRef.current,
          maxAttempts: MAX_RECONNECT_ATTEMPTS,
          reason,
        })
        setters.setError('Connection failed after multiple attempts. Please refresh the page.')
        return
      }

      if (refs.reconnectTimeoutRef.current) return
      refs.reconnectAttemptsRef.current += 1
      const backoff = Math.min(refs.reconnectBackoffRef.current, 10000)

      logger.warn('AI reconnect scheduled', {
        reason,
        backoffMs: backoff,
        attempt: refs.reconnectAttemptsRef.current,
        maxAttempts: MAX_RECONNECT_ATTEMPTS,
      })

      refs.reconnectTimeoutRef.current = setTimeout(() => {
        refs.reconnectTimeoutRef.current = null
        refs.reconnectBackoffRef.current = Math.min(refs.reconnectBackoffRef.current * 1.5, 10000)
        refs.startInterviewRef.current()
      }, backoff)
    },
    [refs, setters, state.showThankYou, state.status]
  )

  const startInterview = useCallback(async () => {
    try {
      if (!refs.mediaStreamRef.current || refs.isConnectingRef.current) return
      refs.isConnectingRef.current = true

      await disconnectExistingService(refs)
      setters.setError(null)
      setters.setStatus(InterviewStatus.CONNECTED)

      const { prompt, questions, liveToken } = await loadInterviewContext({
        interviewId,
        token,
        otpProofToken,
      })

      const service = createLiveService({
        refs,
        setters,
        prompt,
        liveToken,
        interviewId,
        apiBaseUrl,
        scheduleReconnect,
        showNotification,
        startVideoStreaming,
      })

      refs.serviceRef.current = service
      if (questions.length > 0) {
        service.initializeInterviewState(questions)
        logger.info(`Initialized interview state with ${questions.length} questions`)
      }

      await service.connect(refs.mediaStreamRef.current)
      refs.isConnectingRef.current = false
    } catch (err: unknown) {
      refs.isConnectingRef.current = false
      const sessionError =
        err instanceof Error
          ? new SessionError(err.message, 'SESSION_INIT_FAILED')
          : new SessionError(
              'Failed to initialize interview. Please check your API key configuration.',
              'SESSION_INIT_FAILED'
            )
      const result = handleError(sessionError, 'InterviewSession:startInterview')
      setters.setError(result.userMessage)
    }
  }, [
    apiBaseUrl,
    interviewId,
    otpProofToken,
    refs,
    scheduleReconnect,
    setters,
    showNotification,
    startVideoStreaming,
    token,
  ])

  useEffect(() => {
    refs.startInterviewRef.current = () => {
      void startInterview()
    }
  }, [refs.startInterviewRef, startInterview])

  return { startInterview, scheduleReconnect }
}

async function disconnectExistingService(refs: SessionRefs) {
  if (!refs.serviceRef.current) return

  logger.warn('Disconnecting existing service before starting new interview')
  refs.isIntentionallyDisconnectingRef.current = true
  try {
    await refs.serviceRef.current.disconnect()
    refs.serviceRef.current = null
  } catch (err) {
    logger.error('Error during intentional disconnect', { error: String(err) })
  } finally {
    refs.isIntentionallyDisconnectingRef.current = false
  }
}
